/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `formatNotificationMessage` for the Caintra Connect frontend.
 * @module utils/notifications/formatNotificationMessage
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import dayjs from "dayjs";

const buildB2bAppointmentReminderMessage = (notificationData, t) =>
  t("components.notificationsList.b2bEventAppointmentReminderTMinus10", {
    userName: notificationData.nombreUsuario,
    buyerCompanyName: notificationData.nombreEmpresaCompradora,
    buyerUserName: notificationData.nombreUsuarioComprador,
  });

const NOTIFICATION_MESSAGE_BUILDERS = {
  newRequirement: (data, t) =>
    t("components.notificationsList.newRequirement", {
      companyName: data.nombreCompania,
    }),
  proposalSent: (data, t) =>
    t("components.notificationsList.proposalSent", {
      providerName: data.nombreProveedor,
      requirementName: data.nombreRequerimiento,
    }),
  proposalAccepted: (data, t) =>
    t("components.notificationsList.proposalAccepted", {
      companyName: data.nombreCompania,
    }),
  proposalRejected: (data, t) =>
    t("components.notificationsList.proposalRejected", {
      companyName: data.nombreCompania,
      requirementName: data.nombreRequerimiento,
    }),
  proposalClosed: (data, t) =>
    t("components.notificationsList.proposalClosed", {
      requirementName: data.nombreRequerimiento,
    }),
  proposalsPending: (data, t) =>
    t("components.notificationsList.proposalsPending", {
      requirementName: data.nombreRequerimiento,
    }),
  newEvent: (data, t) =>
    t("components.notificationsList.newEvent", {
      eventName: data.nombreEvento,
    }),
  eventUpdate: (data, t) =>
    t("components.notificationsList.eventUpdate", {
      eventName: data.nombreEvento,
    }),
  eventCancel: (data, t) =>
    t("components.notificationsList.eventCancel", {
      eventName: data.nombreEvento,
    }),
  eventRemainder: (data, t) =>
    t("components.notificationsList.eventRemainder", {
      eventName: data.nombreEvento,
    }),
  eventRegister: (data, t) =>
    t("components.notificationsList.eventRegister", {
      eventName: data.nombreEvento,
    }),
  b2bEventRegister: (data, t) =>
    t("components.notificationsList.b2bEventRegister", {
      eventName: data.nombreEvento,
      buyerCompanyName: data.nombreEmpresaCompradora,
    }),
  b2bEventAppointmentCancelled: (_data, t) =>
    t("components.notificationsList.b2bEventAppointmentCancelled"),
  b2bEventAppointmentReminderTMinus10: buildB2bAppointmentReminderMessage,
  b2bEventAppointmentReminderNextTwoTurns: buildB2bAppointmentReminderMessage,
  attendanceCancel: (data, t) =>
    t("components.notificationsList.attendanceCancel", {
      eventName: data.nombreEvento,
    }),
  newMessage: (_data, t) => t("components.notificationsList.newMessage"),
  connectionRequest: (data, t) =>
    t("components.notificationsList.connectionRequest", {
      companyName: data.nombreEmpresaRemitente,
    }),
  connectionRequestAccepted: (data, t) =>
    t("components.notificationsList.connectionRequestAccepted", {
      companyName: data.nombreEmpresaRemitente,
    }),
  connectionRequestAlreadyAccepted: (data, t) =>
    t("components.notificationsList.connectionRequestAlreadyAccepted", {
      userName: data.nombreUsuarioAceptante,
    }),
  connectionRequestRejected: (data, t) =>
    t("components.notificationsList.connectionRequestRejected", {
      companyName: data.nombreEmpresaRechazante,
    }),
  virtualAppointmentProposal: (data, t) =>
    t("components.notificationsList.virtualAppointmentProposal", {
      buyerCompanyName: data.nombreEmpresaCompradora,
    }),
  customerCompanyUserConnectionRequest: (data, t) =>
    t("components.notificationsList.customerCompanyUserConnectionRequest", {
      buyerName: data.nombreUsuarioComprador,
    }),
  appointmentReminderTMinus20: (data, t) =>
    t("components.notificationsList.appointmentReminderTMinus20", {
      buyerCompanyName: data.nombreEmpresaCompradora,
    }),
  appointmentReminderTMinus5: (data, t) =>
    t("components.notificationsList.appointmentReminderTMinus5", {
      buyerCompanyName: data.nombreEmpresaCompradora,
    }),
  appointmentDelay: (data, t) => {
    const newStartDate = dayjs
      .utc(data.nuevaEstimacion)
      .local()
      .format("DD/MM/YYYY HH:mm");
    return t("components.notificationsList.appointmentDelay", {
      buyerCompanyName: data.nombreEmpresaCompradora,
      newStartDate,
    });
  },
  requirementDeleted: (data, t) =>
    t("components.notificationsList.requirementDeleted", {
      requirementName: data.nombreRequerimiento,
      rejectionReason: data.mensaje,
    }),
  customNotification: (data, t) =>
    t("components.notificationsList.notificationDescription", {
      description: data.descripcion,
    }),
};

/**
 * Formats a notification message for display in the bell dropdown.
 * @param {Object} notification - Notification record
 * @param {Function} t - i18n translate function
 * @returns {string}
 */
export const formatNotificationMessage = (notification, t) => {
  const notificationData = notification.datosMensaje || {};
  const builder = NOTIFICATION_MESSAGE_BUILDERS[notification.tipo];

  if (!builder) {
    return t("components.notificationsList.defaultMessage");
  }

  return builder(notificationData, t);
};
